import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { modelCacheManager } from '../controllers/model-cache-manager.js';

// LOADER CONDIVISI (singleton)
const _sharedDracoLoader = new DRACOLoader();
_sharedDracoLoader.setDecoderPath('/draco/');
_sharedDracoLoader.preload();

const _sharedLoader = new GLTFLoader();
_sharedLoader.setDRACOLoader(_sharedDracoLoader);

class Furniture {
  constructor(catalogItem, position = new THREE.Vector3(0, 0, 0)) {
    this.id          = this.generateId();
    this.catalogItem = catalogItem;
    this.position    = position.clone();
    this.rotation    = 0;
    this.scale       = 1.0;

    this.mesh        = null;
    this.boundingBox = null;
    this.isSelected  = false;
    this.isLoaded    = false;

    this.outlineMesh = null;
  }

  generateId() {
    return 'furniture_' + Math.random().toString(36).substr(2, 9);
  }

  // LOAD
  async load() {
    if (this.isLoaded) return this.mesh;

    if (this.catalogItem.type === 'door' || this.catalogItem.type === 'window') {
      this.createProceduralMesh();
      this.isLoaded = true;
      return this.mesh;
    }

    if (this.catalogItem.isCustom && this.catalogItem.modelData) {
      return this.loadCustomModel();
    }

    if (!this.catalogItem.model) {
      console.error('Nessun modello specificato per:', this.catalogItem.name);
      return null;
    }

    return this._loadStandardModel();
  }

  /**
   * Carica un modello GLTF standard tramite ModelCacheManager
   *
   * Scenari:
   *  A) Cache hit          → clone istantaneo, zero rete
   *  B) Fetch già in corso → aggancio alla Promise in-flight
   *  C) Prima richiesta    → avvia fetch, registra Promise, popola cache
   */
  _loadStandardModel() {
    const modelPath = `/models/gltf/${this.catalogItem.model}`;

    // A) Cache hit
    const cached = modelCacheManager.get(modelPath);
    if (cached) {
      return this._finalizeGltfScene(cached);
    }

    // B) Fetch già avviata
    if (modelCacheManager.hasInflight(modelPath)) {
      return modelCacheManager.getInflight(modelPath).then(() => {
        const clone = modelCacheManager.get(modelPath);
        // Fallback: se il modello era troppo grande per la cache, ricarica da zero
        if (!clone) return this._loadWithoutCache(modelPath);
        return this._finalizeGltfScene(clone);
      });
    }

    // C) Prima richiesta
    const promise = new Promise((resolve, reject) => {
      _sharedLoader.load(
        modelPath,
        (gltf) => {
          // Tenta di mettere in cache; se troppo grande, ritorna false
          const cached = modelCacheManager.set(modelPath, gltf.scene, this.catalogItem.name);
          if (!cached) {
            // Modello troppo grande: usa la scena direttamente (non cachata)
            this._oversizedScene = gltf.scene;
          }
          modelCacheManager.deleteInflight(modelPath);
          resolve();
        },
        undefined,
        (error) => {
          modelCacheManager.deleteInflight(modelPath);
          console.error(`Errore caricamento ${this.catalogItem.name}:`, error);
          reject(error);
        }
      );
    });

    modelCacheManager.setInflight(modelPath, promise);

    return promise.then(() => {
      if (this._oversizedScene) {
        // Modello non in cache: finalizza con la scena originale
        const scene = this._oversizedScene;
        this._oversizedScene = null;
        return this._finalizeGltfScene(scene);
      }
      const clone = modelCacheManager.get(modelPath);
      return this._finalizeGltfScene(clone);
    });
  }

  /**
   * Fallback: carica il modello senza tentare di usare la cache
   * Usato quando il modello è già in volo ma non finisce in cache
   */
  _loadWithoutCache(modelPath) {
    return new Promise((resolve, reject) => {
      _sharedLoader.load(
        modelPath,
        (gltf) => resolve(this._finalizeGltfScene(gltf.scene)),
        undefined,
        (err) => reject(err)
      );
    });
  }

  // Carica un modello custom da ArrayBuffer con cache e deduplicazione in-flight
  loadCustomModel() {
    const cacheKey = `custom_${this.catalogItem.id}`;

    // Cache hit
    const cached = modelCacheManager.get(cacheKey);
    if (cached) return this._finalizeGltfScene(cached);

    // In-flight
    if (modelCacheManager.hasInflight(cacheKey)) {
      return modelCacheManager.getInflight(cacheKey).then(() => {
        const clone = modelCacheManager.get(cacheKey);
        return clone
          ? this._finalizeGltfScene(clone)
          : this._parseCustomFromData(); // fallback se non cachato
      });
    }

    // Prima richiesta
    const promise = new Promise((resolve, reject) => {
      _sharedLoader.parse(
        this.catalogItem.modelData,
        '',
        (gltf) => {
          modelCacheManager.set(cacheKey, gltf.scene, this.catalogItem.name);
          modelCacheManager.deleteInflight(cacheKey);
          console.log('✓ Modello custom caricato:', this.catalogItem.name);
          resolve();
        },
        (error) => {
          modelCacheManager.deleteInflight(cacheKey);
          console.error(`Errore caricamento modello custom ${this.catalogItem.name}:`, error);
          reject(error);
        }
      );
    });

    modelCacheManager.setInflight(cacheKey, promise);

    return promise.then(() => {
      const clone = modelCacheManager.get(cacheKey);
      return clone
        ? this._finalizeGltfScene(clone)
        : this._parseCustomFromData();
    });
  }

  // Parsing diretto senza cache (fallback per modelli custom oversized)
  _parseCustomFromData() {
    return new Promise((resolve, reject) => {
      _sharedLoader.parse(
        this.catalogItem.modelData,
        '',
        (gltf) => resolve(this._finalizeGltfScene(gltf.scene)),
        (err) => reject(err)
      );
    });
  }

  /**
   * Operazioni comuni post-load / post-clone
   * I materiali vengono clonati per garantire copie indipendenti per istanza
   */
  _finalizeGltfScene(gltfScene) {
    this.mesh = new THREE.Group();
    this.mesh.add(gltfScene);

    this.centerModel();
    this.updateTransform();
    this.updateBoundingBox();

    this.mesh.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = false;
        child.receiveShadow = false;

        // Copia indipendente dei materiali per questa istanza
        if (Array.isArray(child.material)) {
          child.material = child.material.map(m => m.clone());
        } else if (child.material) {
          child.material = child.material.clone();
        }

        // Costruisce la BVH sulla geometria se non è già presente
        // computeBoundsTree() è aggiunto da three-mesh-bvh in engine.js
        // La BVH viene condivisa tra tutti i clone(true) dello stesso template in cahe:
        // viene costruita una volta sola al primo clone e riutilizzata
        // da tutte le istanze successive → zero overhead ripetuto
        if (child.geometry && !child.geometry.boundsTree) {
          child.geometry.computeBoundsTree();
        }
      }
    });

    this.mesh.userData.furniture = this;
    this.isLoaded = true;
    return this.mesh;
  }

  // MESH PROCEDURALE (porte / finestre placeholder)
  createProceduralMesh() {
    this.mesh = new THREE.Group();

    const geometry = new THREE.BoxGeometry(
      this.catalogItem.dimensions.width,
      this.catalogItem.dimensions.height,
      this.catalogItem.dimensions.depth
    );

    const material = new THREE.MeshStandardMaterial({
      color:       this.catalogItem.type === 'door' ? 0xd4a574 : 0x88ccff,
      transparent: true,
      opacity:     0.8,
      roughness:   0.6,
      metalness:   0.2
    });

    const box = new THREE.Mesh(geometry, material);
    box.castShadow = false;
    box.receiveShadow = false;

    this.mesh.add(box);
    this.mesh.userData.furniture = this;

    this.updateTransform();
    this.updateBoundingBox();
  }

  // TRANSFORM / BOUNDING BOX
  centerModel() {
    if (!this.mesh) return;
    const box    = new THREE.Box3().setFromObject(this.mesh);
    const center = box.getCenter(new THREE.Vector3());
    this.mesh.position.x = -center.x;
    this.mesh.position.z = -center.z;
    this.mesh.position.y = -box.min.y;
  }

  updateTransform() {
    if (!this.mesh) return;
    this.mesh.position.set(this.position.x, this.position.y, this.position.z);
    this.mesh.rotation.y = THREE.MathUtils.degToRad(this.rotation);
    this.mesh.scale.setScalar(this.scale);
  }

  updateBoundingBox() {
    if (!this.mesh) return;
    this.boundingBox = new THREE.Box3().setFromObject(this.mesh);
  }

  // POSIZIONE / ROTAZIONE / SCALA
  setPosition(x, y, z) {
    this.position.set(x, y, z);
    this.updateTransform();
    this.updateBoundingBox();
  }

  move(deltaX, deltaY, deltaZ) {
    this.position.x += deltaX;
    this.position.y += deltaY;
    this.position.z += deltaZ;
    this.updateTransform();
    this.updateBoundingBox();
  }

  rotate(degrees) {
    const step     = this.catalogItem.rotationStep || 15;
    this.rotation  = (this.rotation + degrees) % 360;
    if (this.rotation < 0) this.rotation += 360;
    this.rotation  = Math.round(this.rotation / step) * step;
    this.updateTransform();
    this.updateBoundingBox();
  }

  setRotation(degrees) {
    this.rotation = degrees % 360;
    if (this.rotation < 0) this.rotation += 360;
    this.updateTransform();
    this.updateBoundingBox();
  }

  setScale(scale) {
    this.scale = scale;
    this.updateTransform();
    this.updateBoundingBox();
  }

  // SELEZIONE
  select() {
    this.isSelected = true;
    this.createSelectionVisuals();
  }

  deselect() {
    this.isSelected = false;
    this.removeSelectionVisuals();
  }

  createSelectionVisuals() {
    if (!this.mesh) return;
    this.mesh.traverse((child) => {
      if (child.isMesh && child.material) {
        const mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach(m => { if (m.emissive) m.emissive.setHex(0xddeeff); });
      }
    });
  }

  removeSelectionVisuals() {
    if (!this.mesh) return;
    this.mesh.traverse((child) => {
      if (child.isMesh && child.material) {
        const mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach(m => { if (m.emissive) m.emissive.setHex(0x000000); });
      }
    });
  }

  // UTILS
  getBoundingBox()  { return this.boundingBox; }

  getDimensions() {
    if (!this.boundingBox) return null;
    return new THREE.Vector3().subVectors(this.boundingBox.max, this.boundingBox.min);
  }

  clone() {
    const cloned    = new Furniture(this.catalogItem, this.position.clone());
    cloned.rotation = this.rotation;
    cloned.scale    = this.scale;
    return cloned;
  }

  serialize() {
    return {
      id:            this.id,
      catalogItemId: this.catalogItem.id,
      position:      { x: this.position.x, y: this.position.y, z: this.position.z },
      rotation:      this.rotation,
      scale:         this.scale
    };
  }

  static deserialize(data, catalogItem) {
    const furniture    = new Furniture(
      catalogItem,
      new THREE.Vector3(data.position.x, data.position.y, data.position.z)
    );
    furniture.id       = data.id;
    furniture.rotation = data.rotation;
    furniture.scale    = data.scale;
    return furniture;
  }

  // PRELOAD CONTROLLATO
  /**
   * Precarica in background tutti i modelli standard di un catalogo
   * Delega interamente a ModelCacheManager che gestisce budget e priorità
   *
   * @param {object}   catalog
   * @param {Function} [onProgress]  callback(loaded, total, name)
   */
  static async preloadAll(catalog, onProgress) {
    await modelCacheManager.preloadCatalog(catalog, _sharedLoader, onProgress);
  }

  //Svuota la cache globale dei modelli e libera le risorse
  static clearCache() {
    modelCacheManager.clear();
  }

  // Restituisce le statistiche della cache
  static getCacheStats() {
    return modelCacheManager.getStats();
  }

  // Log tabellare delle statistiche
  static logCacheStats() {
    modelCacheManager.logStats();
  }

  // DISPOSE
  dispose() {
    this.removeSelectionVisuals();
    if (this.mesh) {
      this.mesh.traverse((child) => {
        if (child.geometry) {
          // Libera la BVH prima di disporre la geometria
          // disposeBoundsTree() è aggiunto da three-mesh-bvh in engine.js
          // Evita memory leak nella struttura dati accelerata
          if (child.geometry.boundsTree) {
            child.geometry.disposeBoundsTree();
          }
          child.geometry.dispose();
        }
        if (child.material) {
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          mats.forEach(m => m.dispose());
        }
      });
    }
  }
}

export default Furniture;